import { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import Actionbar from 'src/components/common/actionbar/Authactionbar';
import { Message } from 'src/components/common/form/Message';
import useUiuxStore from 'src/store/useUiuxStore';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { HRM1040_R01Content } from './Content_HRM1040_R01';
import { HRM1040_R01EducationDto, HRM1040_R01FamilyDto, HRM1040_R01HeaderGridRow } from './Dto_HRM1040_R01';
import { EducationTable_HRM1040_R01 } from './EducationTable_HRM1040_R01';
import { FamilyTable_HRM1040_R01 } from './FamilyTable_HRM1040_R01';
import { HRM1040_R01SearchSection } from './SearchSection_HRM1040_R01';
import { Table_HRM1040_R01 } from './Table_HRM1040_R01';

/** 사원 목록 왼쪽 너비(%)의 저장 키와 최소·최대·기본값이다. */
const WIDTH_RESIZER = {
    code: 'HRM1040_R01',
    type: 'width',
    storageKey: 'HRM1040_R01-split-width',
    min: 25,
    max: 65,
    default: 40,
} as const;
/** 가족 영역 위쪽 높이(%)의 저장 키와 최소·최대·기본값이다. */
const HEIGHT_RESIZER = {
    code: 'HRM1040_R01',
    type: 'height',
    storageKey: 'HRM1040_R01-split-height',
    min: 20,
    max: 80,
    default: 50,
} as const;

/**
 * 가족·학력 화면을 액션바, 검색, 사원 목록, 두 편집 그리드로 조립한다.
 * @returns 사원 목록(좌)과 가족·학력 그리드(우, 위아래)로 나뉜 화면 JSX
 */
export const Container_HRM1040_R01 = () => {
    // 저장 전 편집을 확정할 세 그리드의 참조
    const gridRef = useRef<AgGridReact<HRM1040_R01HeaderGridRow>>(null);
    const familyGridRef = useRef<AgGridReact<HRM1040_R01FamilyDto>>(null);
    const educationGridRef = useRef<AgGridReact<HRM1040_R01EducationDto>>(null);

    // 좌우 및 상하 분할 영역의 참조와 드래그 상태
    const widthContainerRef = useRef<HTMLDivElement>(null);
    const widthInfoRef = useRef<HTMLDivElement>(null);
    const widthResizing = useRef(false);
    const heightContainerRef = useRef<HTMLDivElement>(null);
    const heightInfoRef = useRef<HTMLDivElement>(null);
    const heightResizing = useRef(false);

    const { splitValue: width, handleMouseDown: startWidth } = useMouseContent({
        resizer: WIDTH_RESIZER,
        isResizing: widthResizing,
        containerRef: widthContainerRef,
        infoRef: widthInfoRef,
    });
    const { splitValue: height, handleMouseDown: startHeight } = useMouseContent({
        resizer: HEIGHT_RESIZER,
        isResizing: heightResizing,
        containerRef: heightContainerRef,
        infoRef: heightInfoRef,
    });

    const { setMessage } = useUiuxStore();
    const {
        fetchData,
        handleHeaderRowClick,
        handleDeleteClick,
        handleSaveClick,
        handleExcelClick,
        initContainerData,
    } = HRM1040_R01Content({ gridRef, familyGridRef, educationGridRef, setMessage });
    return (
        <div className="content-container">
            {/* 공통 액션바. 행 추가는 각 그리드의 +로 하므로 등록은 연결하지 않고 메뉴 권한으로 끈다. */}
            <Actionbar onFetchClick={fetchData} onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick} onExcelClick={handleExcelClick} onInitClick={initContainerData} />
            {/* 사원 검색 조건은 공통 사원 선택 팝업을 사용한다. */}
            <HRM1040_R01SearchSection />
            <div className="flex flex-1 min-h-0 p-[12px] gap-[4px]" ref={widthContainerRef}>
                <div className="min-w-0 min-h-0" style={{ width: `${width}%` }}>
                    {/* 조회 전용 사원 목록. 행 클릭 시 선택 사원을 바꾼다. */}
                    <Table_HRM1040_R01 gridRef={gridRef} onSelect={handleHeaderRowClick} />
                </div>
                {/* 좌우 크기 조절 경계선 */}
                <div className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                    onMouseDown={event => startWidth('LR', event.nativeEvent)} />
                <div className="flex flex-col min-w-0 min-h-0 gap-[4px]" style={{ width: `${100 - width}%` }}
                    ref={widthInfoRef}>
                    <div className="flex flex-col flex-1 min-h-0 gap-[4px]" ref={heightContainerRef}>
                        <div className="min-h-0" style={{ height: `${height}%` }}>
                            {/* 선택 사원의 가족관계 편집 그리드. 공통코드와 행 추가는 그리드가 직접 처리한다. */}
                            <FamilyTable_HRM1040_R01 gridRef={familyGridRef} />
                        </div>
                        {/* 위아래 크기 조절 경계선 */}
                        <div className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
                            onMouseDown={event => startHeight('UD', event.nativeEvent)} />
                        <div className="min-h-0" style={{ height: `${100 - height}%` }} ref={heightInfoRef}>
                            {/* 선택 사원의 학력 편집 그리드. 공통코드와 행 추가는 그리드가 직접 처리한다. */}
                            <EducationTable_HRM1040_R01 gridRef={educationGridRef} />
                        </div>
                    </div>
                </div>
            </div>
            {/* 하단 결과 메시지 */}
            <Message />
        </div>
    );
};
